import { Injectable, inject } from '@angular/core';
import { APP_BASE_HREF, PathLocationStrategy, PlatformLocation } from '@angular/common';
import { CustomDomainContextService } from './custom-domain-context.service';

/**
 * LocationStrategy para dominios personalizados: hace el slug del restaurante un
 * prefijo TRANSPARENTE. El router internamente trabaja con `/{slug}/...` (así toda
 * la navegación existente —router.navigate(['/', slug, ...]) y routerLink— sigue
 * funcionando sin cambios), pero el navegador muestra la URL SIN el slug.
 *
 * Mapeo especial para que la raíz sea la landing:
 *   navegador  /          <->  interno /{slug}/info   (landing)
 *   navegador  /menu      <->  interno /{slug}         (menú, default del restaurante)
 *   navegador  /X         <->  interno /{slug}/X       (resto: /menum, /pos, /cocina, ...)
 *
 * Como el slug se fuerza siempre al del dominio, es imposible cargar otro
 * restaurante (aislamiento estructural).
 */
@Injectable()
export class CustomDomainLocationStrategy extends PathLocationStrategy {
  private readonly ctx = inject(CustomDomainContextService);

  constructor() {
    super(inject(PlatformLocation), inject(APP_BASE_HREF, { optional: true }) ?? undefined);
  }

  private get slug(): string {
    return this.ctx.slug() ?? '';
  }

  override path(includeHash = false): string {
    const browser = super.path(includeHash);
    const slug = this.slug;
    if (!slug) {
      return browser;
    }
    const [p, suffix] = this.splitSuffix(browser);
    return this.toInternal(p, slug) + suffix;
  }

  override prepareExternalUrl(internal: string): string {
    const full = super.prepareExternalUrl(internal);
    const slug = this.slug;
    if (!slug) {
      return full;
    }
    const [p, suffix] = this.splitSuffix(full);
    return this.toBrowser(p, slug) + suffix;
  }

  /** Separa el path del query/hash (todo desde el primer ? o #). */
  private splitSuffix(url: string): [string, string] {
    const i = url.search(/[?#]/);
    return i === -1 ? [url, ''] : [url.substring(0, i), url.substring(i)];
  }

  /** navegador -> interno (lo que ve el router): antepone /{slug}. */
  private toInternal(path: string, slug: string): string {
    if (path === '' || path === '/') {
      return `/${slug}/info`;
    }
    if (path === '/menu') {
      return `/${slug}`;
    }
    return `/${slug}${path}`;
  }

  /** interno -> navegador: quita /{slug}. */
  private toBrowser(path: string, slug: string): string {
    const prefix = `/${slug}`;
    if (path === `${prefix}/info`) {
      return '/';
    }
    if (path === prefix) {
      return '/menu';
    }
    if (path.startsWith(`${prefix}/`)) {
      return path.substring(prefix.length);
    }
    return path;
  }
}
